import { CellMap } from "./CellMap";
import { MaterialPile, ProgressionState } from "./GameState";
import { CleaningGear, heldTool } from "./HeldTool";
import { atTruckBed, atTruckCab } from "./lot";
import { atStand } from "./stand";
import { Machine, machineKey, MachineState } from "./Machine";
import { getMaterialName } from "./material-helpers";
import { MaterialInstance } from "./Materials";
import { handSpaceLeft, Person } from "./Person";
import { pileWithinReach } from "./pile-helpers";
import { ShopInfo } from "./ShopInfo";
import { divesToBench, hasStationSheet } from "./station-helpers";
import { chebyshevDistance } from "./Vectors";
import { mod } from "../utils/mathUtils";

/**
 * What pressing E (the interact key) would do right now. One resolver
 * shared by the keyboard handler, the on-screen hints, and the
 * targeting outline, so the chip next to the player and the rim on the
 * canvas always name exactly what the key will do.
 *
 * E is the one key for nearly everything, so what it means at a spot is
 * a *ring* of targets (`interactRing`) with a cursor on it: the top of
 * the ring is what a plain press does, and the cursor moves off it only
 * when the player points at something else — the mouse hovering a
 * piece, or R stepping the outline along. Exactly one thing wears the
 * outline at a time, and E acts on that thing.
 *
 * The ring's order is the priority: a bench with hand work goes first,
 * so E at a bench leans over it unless a piece is pointed at; then the
 * pieces in reach (a machine's outfeed, its loaded bay, the floor); and
 * a station's sheet — a container's contents, a direct-feed machine's
 * accessory rack — comes last, since a can standing in a whole ring of
 * cells' reach must not take the key from a board underfoot, and
 * running a saw is the floor's business. When the ring is empty, E
 * falls through to the things that need no pointing: the broom, the
 * vac, the truck's bed, the stand, the cab, a running machine's switch.
 */
export type InteractAction =
  /** Lean over the bench's work surface, or spread a station's sheet
   * (a container's contents, a direct-feed machine's accessories). */
  | { kind: "open-station"; machine: Machine }
  /** One finished piece at a machine's outfeed — `material` — the one a
   * plain press takes; Shift takes the rest of the outfeed after it. */
  | { kind: "take-outputs"; machine: Machine; material: MaterialInstance }
  /** One piece off a machine's loaded bay or a bench's top — `material`;
   * Shift takes the rest after it. */
  | { kind: "take-inputs"; machine: Machine; material: MaterialInstance }
  | { kind: "switch-on"; machine: Machine }
  | { kind: "switch-off"; machine: Machine }
  /** Newest-dropped first, so `piles[0]` is the top of the pile. `target`
   * is what a plain press grabs — the top by default, stepped through the
   * rest by the rummage offset (R) or the cursor; Shift takes them all,
   * starting at the target. */
  | {
      kind: "pick-up-floor";
      piles: ReadonlyArray<MaterialPile>;
      target: MaterialPile;
    }
  | { kind: "pick-up-broom" }
  /** The parked vac is a step away: E takes up its hose. */
  | { kind: "pick-up-vac" }
  /** Standing at the truck's bed with cargo in it: E lifts the last
   * piece loaded back out — `material` — and Shift empties the bed. */
  | { kind: "truck-bed"; count: number; material: MaterialInstance }
  /** Standing at the for-sale stand with pieces on it: E takes the
   * newest one back — `material` — and Shift clears the table. */
  | { kind: "stand"; count: number; material: MaterialInstance }
  /** Standing at the stand's spot before the table is out: E sets it
   * up. Only once the story has offered the spot. */
  | { kind: "set-up-stand" }
  | { kind: "truck-cab" };

/**
 * One entry of the ring E's cursor steps around: the station to use, a
 * piece to take from a machine (one entry per piece, so the cursor can
 * land on any of them), the machine's switch, or a piece lying on the
 * floor. In the order the resolver would pick them — so a bench's stock
 * never *hides* the board on the floor beside it, it just goes first.
 */
export type InteractTarget =
  | { kind: "open-station"; machine: Machine }
  | { kind: "take-outputs"; machine: Machine; material: MaterialInstance }
  | { kind: "take-inputs"; machine: Machine; material: MaterialInstance }
  | { kind: "switch-on"; machine: Machine }
  | { kind: "floor-pile"; pile: MaterialPile };

/** A stable identity for a ring entry, for spotting when the ring itself
 * has changed (a piece taken, a bay emptied, a different machine faced) —
 * that's when the cursor goes back to the top. */
export function interactTargetKey(target: InteractTarget): string {
  switch (target.kind) {
    case "open-station":
      return `open:${machineKey(target.machine.state)}`;
    case "take-outputs":
      return `out:${machineKey(target.machine.state)}:${target.material.id}`;
    case "take-inputs":
      return `in:${machineKey(target.machine.state)}:${target.material.id}`;
    case "switch-on":
      return `switch:${machineKey(target.machine.state)}`;
    case "floor-pile":
      return `pile:${target.pile.material.id}`;
  }
}

/**
 * What the interact resolvers read — a structural slice of `GameState`
 * (issue #230, phase 4). The floor's cell index travels alongside as an
 * explicit `CellMap` argument: snapshot holders build one with
 * `CellMap.fromGameState`, live callers pass the ShopGrid's.
 */
export interface InteractFacts extends CleaningGear {
  readonly player: Pick<
    Person,
    "away" | "carriedMachine" | "position" | "inventory"
  >;
  readonly materialPiles: ReadonlyArray<MaterialPile>;
  readonly truck: {
    readonly bed: ReadonlyArray<MaterialInstance>;
    readonly crates: ReadonlyArray<MachineState>;
  };
  readonly stand: ReadonlyArray<MaterialInstance>;
  /** Whether the table is out by the road (see StandEntity). */
  readonly standSetUp: boolean;
  /** Whether the story has offered the stand's spot yet, and which
   * skills are known (a bench with no hand work unlocked is a table). */
  readonly progression: ProgressionState;
  readonly shopInfo: ShopInfo;
}

/**
 * Why the material verbs (take, pick up) are off right now: a tool
 * committing the hands, or arms already at capacity. Null when the hands
 * could take something. The chips show this text under the dimmed verb
 * they can't offer (see `blockedInteract`), so a full-handed player
 * standing over stock reads "pick up · arms full" instead of silence.
 */
export function takeBlockedReason(gameState: InteractFacts): string | null {
  switch (heldTool(gameState)) {
    case "broom":
      return "put the broom down first";
    case "vacHose":
      return "set the vac down first";
  }
  return handSpaceLeft(gameState.player) > 0 ? null : "arms full";
}

/**
 * Where a station's "use" sits on the ring. A bench with hand work is
 * used *through* its top, so E means that first and a piece has to be
 * pointed at. A sheet — a container's contents, a direct-feed machine's
 * accessory rack — comes last, after every piece the key could take: a
 * garbage can is reachable from a whole ring of cells, since it has no
 * front, and must not swallow E from a board at your feet; a saw is run
 * from the floor and its rack is a rare errand.
 */
function openPlacement(
  machine: Machine,
  progression: ProgressionState,
): "first" | "last" | null {
  if (divesToBench(machine, progression)) return "first";
  return hasStationSheet(machine) ? "last" : null;
}

/**
 * Every target E could act on here, in priority order (see the header).
 * The take entries are left off when the hands aren't free to take (a
 * held tool, full arms) — unless `ignoreHands`, which `blockedInteract`
 * uses to find what a blocked take *would* land on, so the explanation
 * can sit under the verb it's keeping off.
 */
export function interactRing(
  gameState: InteractFacts,
  cellMap: CellMap,
  targetedMachine: Machine | undefined,
  ignoreHands = false,
): ReadonlyArray<InteractTarget> {
  if (gameState.player.away || gameState.player.carriedMachine != null) {
    return [];
  }

  // A tool in hand commits the hands: material verbs (take, unload, pick
  // up the floor) step aside until it's set down. Full arms step the same
  // verbs aside: the chip never offers a pickup the action would refuse.
  const takesLive = ignoreHands || takeBlockedReason(gameState) === null;

  const ring: InteractTarget[] = [];
  const opens = targetedMachine
    ? openPlacement(targetedMachine, gameState.progression)
    : null;
  if (targetedMachine && opens === "first") {
    ring.push({ kind: "open-station", machine: targetedMachine });
  }

  if (takesLive) {
    const cell = cellMap.at(gameState.player.position);

    const candidates = dedupeMachines(
      [targetedMachine, ...(cell?.operableMachines ?? [])]
        .filter((machine) => machine != null)
        // A container is opened, not reached into: what you toss in comes
        // back out through its contents sheet, which is what E opens.
        .filter((machine) => !machine.type.container),
    );

    // Outputs are collected where they land: at this cell for machines
    // whose outfeed points here, at the machine itself for single-point
    // stations (no outputPosition).
    const outputSources = dedupeMachines([
      ...(cell?.outputMachines ?? []),
      ...candidates.filter(
        (machine) => machine.type.outputPosition === undefined,
      ),
    ]);

    for (const machine of outputSources) {
      for (const material of machine.outputMaterials) {
        ring.push({ kind: "take-outputs", machine, material });
      }
    }
    for (const machine of candidates) {
      for (const material of machine.inputMaterials) {
        ring.push({ kind: "take-inputs", machine, material });
      }
    }
  }

  if (
    targetedMachine?.type.powerSwitch === true &&
    !targetedMachine.isPowered
  ) {
    ring.push({ kind: "switch-on", machine: targetedMachine });
  }

  if (takesLive) {
    // materialPiles keeps drop order (oldest first), and a stack renders
    // in that order too — so the last piece dropped is drawn on top.
    // Reversed here so a plain press takes the top of the pile, and
    // dropping a piece then picking it back up is a round trip.
    const reachable = gameState.materialPiles
      .filter((pile) => pileWithinReach(pile, gameState.player.position))
      .reverse();
    for (const pile of reachable) {
      ring.push({ kind: "floor-pile", pile });
    }
  }

  if (targetedMachine && opens === "last") {
    ring.push({ kind: "open-station", machine: targetedMachine });
  }

  return ring;
}

/**
 * The offset that makes `resolveInteract` land on `target` — what the
 * cursor sets when it points at a specific piece (or at the station
 * itself), instead of stepping R around the ring until the outline
 * lands there. `null` when that target isn't on the ring at all.
 */
export function offsetForTarget(
  gameState: InteractFacts,
  cellMap: CellMap,
  targetedMachine: Machine | undefined,
  target: InteractTarget,
): number | null {
  const ring = interactRing(gameState, cellMap, targetedMachine);
  const key = interactTargetKey(target);
  const index = ring.findIndex((entry) => interactTargetKey(entry) === key);
  return index === -1 ? null : index;
}

/** The leaning broom is a step away — where the pick-up offer (or the
 * note explaining why the hands can't take it) belongs. */
export function broomWithinReach(
  gameState: Pick<InteractFacts, "broomOwned" | "broomPosition" | "player">,
): boolean {
  return (
    gameState.broomOwned &&
    gameState.broomPosition !== null &&
    chebyshevDistance(gameState.broomPosition, gameState.player.position) <= 1
  );
}

/** The parked vac is a step away — where E offers its hose. */
export function vacWithinReach(
  gameState: Pick<InteractFacts, "shopVac" | "player">,
): boolean {
  return (
    gameState.shopVac?.position != null &&
    chebyshevDistance(gameState.shopVac.position, gameState.player.position) <=
      1
  );
}

function dedupeMachines(machines: ReadonlyArray<Machine>): Machine[] {
  const seen = new Set<string>();
  return machines.filter((machine) => {
    const key = machineKey(machine.state);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * `ignoreHands` resolves as if the hands were free to take — the same
 * pretence `interactRing` allows, for `blockedInteract` to find the
 * verb the hands are keeping off. Everything else about the answer is
 * as live as ever, so a `resolveInteract(..., true)` that lands on a
 * take is exactly what the free-handed player at this spot would see.
 */
export function resolveInteract(
  gameState: InteractFacts,
  cellMap: CellMap,
  targetedMachine: Machine | undefined,
  sourceOffset = 0,
  ignoreHands = false,
): InteractAction | null {
  if (gameState.player.away || gameState.player.carriedMachine != null) {
    return null;
  }

  const ring = interactRing(gameState, cellMap, targetedMachine, ignoreHands);
  if (ring.length > 0) {
    const target = ring[mod(sourceOffset, ring.length)];
    if (target.kind !== "floor-pile") return target;
    const piles = ring
      .filter((entry) => entry.kind === "floor-pile")
      .map((entry) => entry.pile);
    return { kind: "pick-up-floor", piles, target: target.pile };
  }

  const handsFree =
    ignoreHands ||
    (heldTool(gameState) === null && handSpaceLeft(gameState.player) > 0);

  // The broom leans where it was left; picking it up needs empty hands
  // (and a free shoulder). Floor pickups outrank it so a pile lying at
  // the broom's feet — the dustpan moment — still gets E first.
  if (
    broomWithinReach(gameState) &&
    handsFree &&
    gameState.player.inventory.length === 0
  ) {
    return { kind: "pick-up-broom" };
  }

  // The vac's hose takes a hand, so it wants no other tool in it.
  if (vacWithinReach(gameState) && heldTool(gameState) === null) {
    return { kind: "pick-up-vac" };
  }

  if (
    handsFree &&
    gameState.truck.bed.length > 0 &&
    atTruckBed(gameState.shopInfo, gameState.player.position)
  ) {
    return {
      kind: "truck-bed",
      count: gameState.truck.bed.length,
      // The last piece loaded is the one on top of the heap — what a
      // plain press lifts back out, and what wears the outline.
      material: gameState.truck.bed[gameState.truck.bed.length - 1],
    };
  }

  if (
    handsFree &&
    gameState.standSetUp &&
    gameState.stand.length > 0 &&
    atStand(gameState.shopInfo, gameState.player.position)
  ) {
    return {
      kind: "stand",
      count: gameState.stand.length,
      // The newest piece set out is what a plain press takes back, and
      // what wears the outline.
      material: gameState.stand[gameState.stand.length - 1],
    };
  }

  // The stand's spot, once the story has pointed at it and before the
  // table is out: E sets it up. Whatever the hands hold — the table is
  // dragged out, not carried.
  if (
    gameState.progression.standUnlocked &&
    !gameState.standSetUp &&
    atStand(gameState.shopInfo, gameState.player.position)
  ) {
    return { kind: "set-up-stand" };
  }

  // The cab always answers: scavenging is on offer from day one.
  if (atTruckCab(gameState.shopInfo, gameState.player.position)) {
    return { kind: "truck-cab" };
  }

  if (targetedMachine?.type.powerSwitch === true && targetedMachine.isPowered) {
    return { kind: "switch-off", machine: targetedMachine };
  }

  return null;
}

/** The interact actions that take material into the hands — the ones a
 * held tool or a full armload keeps off the key. */
const TAKE_KINDS: ReadonlySet<InteractAction["kind"]> = new Set([
  "take-outputs",
  "take-inputs",
  "pick-up-floor",
  "truck-bed",
  "stand",
]);

/**
 * The take the hands are keeping E from: the first take on the ring at
 * this spot if the hands were free, with the reason they aren't. Null
 * while the hands could take (the live resolver's answer is then the
 * whole story), and null when nothing here is a take at all — a switch,
 * the cab — since those keys are live and need no excuse. The chips draw
 * the verb dimmed with the reason under it, on the same subject the
 * live verb would have sat on: the piece, the bench, the bed, the stand.
 *
 * The broom's pickup isn't a take here: it wants completely empty hands,
 * a stricter bar with its own wording (PlayerPrompt).
 */
export function blockedInteract(
  gameState: InteractFacts,
  cellMap: CellMap,
  targetedMachine: Machine | undefined,
): { action: InteractAction; reason: string } | null {
  const reason = takeBlockedReason(gameState);
  if (reason === null) return null;
  const ring = interactRing(gameState, cellMap, targetedMachine, true);
  const index = ring.findIndex((entry) =>
    TAKE_KINDS.has(entry.kind as InteractAction["kind"]),
  );
  const action =
    index === -1
      ? resolveInteract(gameState, cellMap, targetedMachine, 0, true)
      : resolveInteract(gameState, cellMap, targetedMachine, index, true);
  return action != null && TAKE_KINDS.has(action.kind)
    ? { action, reason }
    : null;
}

/**
 * The short verb the hint chip shows for an interact action — a verb and
 * nothing more wherever the chip's own title already names what the key
 * acts on (the machine's chips are headed by the machine, the bed's by
 * the bed). It never names the furniture something comes off: "take", not
 * "take from makeshift workbench" — you can see which bench you're at. The
 * piece a loaded machine or the bed would hand back does get named, since
 * that's the one thing the title can't say.
 */
export function interactLabel(action: InteractAction): string {
  switch (action.kind) {
    case "open-station":
      return action.machine.type.directFeed
        ? "accessories"
        : action.machine.type.container
          ? "contents"
          : "use";
    case "take-outputs":
    case "take-inputs":
      return `take ${getMaterialName(action.material)}`;
    case "switch-on":
      return "switch on";
    case "switch-off":
      return "switch off";
    case "pick-up-floor":
      return "pick up";
    case "pick-up-broom":
      return "pick up broom";
    case "pick-up-vac":
      return "grab vac";
    case "truck-bed":
      return `take ${getMaterialName(action.material)}`;
    case "stand":
      return `take back ${getMaterialName(action.material)}`;
    case "set-up-stand":
      return "set up the stand";
    case "truck-cab":
      return "head out";
  }
}
